import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useGetCustomerQuery, useGetIntegratorMeQuery, useGetUserPreferencesQuery } from '@/api/v1';
import { FeatureFlagsUserContext } from '@/context/featureFlagsContext';
import { FeatureFlagsProvider } from '@/context/FeatureFlagsProvider';
import { useClarity } from '@/hooks/useClarity';
import { useLogRocket } from '@/hooks/useLogRocket';
import PrivateRoutes from '@/Routing/Private';
import PublicRoutes from '@/Routing/Public';
import { authSelector } from '@/StateManagement/AuthReducer';

export const AuthenticationGuard: React.FC = (): React.ReactElement | null => {
  const { pathname } = useLocation();
  const { user } = useSelector(authSelector);
  const { identify: identifyClarity } = useClarity();
  const { identify: identifyLogRocket } = useLogRocket();
  const { data: customer } = useGetCustomerQuery(undefined, { skip: !user });
  const { data: userPreferences } = useGetUserPreferencesQuery(undefined, { skip: !user });
  const { data: integratorMe, isLoading: isIntegratorMeLoading } = useGetIntegratorMeQuery(
    undefined,
    {
      skip: !user || !customer?.integratorId,
    },
  );

  // Ensure we load this as soon as possible
  useGetUserPreferencesQuery(undefined, {
    skip: !user,
  });

  React.useEffect((): void => {
    if (user) {
      identifyClarity(user.id, user.sessionId, pathname, `${user.firstName} ${user.lastName}`);
      identifyLogRocket(user.id, {
        name: `${user.firstName} ${user.lastName}`,
        businessName: customer?.businessInfo?.name,
        email: user.email,
        country: customer?.businessInfo?.country,
        language: userPreferences?.language,
        timezone: userPreferences?.timezone,
        theme: userPreferences?.appearance?.theme,
        integratorId: customer?.integratorId,
        group: customer?.group,
      });
    }
  }, [
    customer,
    identifyClarity,
    identifyLogRocket,
    pathname,
    user,
    userPreferences?.appearance?.theme,
    userPreferences?.language,
    userPreferences?.timezone,
  ]);

  const ldUserContext = React.useMemo((): FeatureFlagsUserContext | undefined => {
    if (!customer?.keycloakId) {
      return undefined;
    }

    const fullName = `${customer.firstName} ${customer.lastName}`.trim();
    const emailDomain = customer.email.includes('@')
      ? customer.email.slice(customer.email.indexOf('@') + 1)
      : undefined;

    const userAttributes = {
      key: customer.keycloakId,
      name: fullName || undefined,
      email: customer.email || undefined,
      emailDomain,
      locale: userPreferences?.language,
      timezone: userPreferences?.timezone,
      _meta: { privateAttributes: ['email', 'name'] },
    };

    if (!customer.integratorId) {
      return { kind: 'user', ...userAttributes };
    }

    // Wait only for the *first* integrator-me fetch to settle (success or
    // error). On error, degrade to a key-only integrator context so flags
    // still evaluate — losing only the `name` enrichment, not all gating.
    if (isIntegratorMeLoading) {
      return undefined;
    }

    return {
      kind: 'multi',
      user: userAttributes,
      integrator: {
        key: customer.integratorId,
        name: integratorMe?.name,
        country: customer.businessInfo?.country,
      },
    };
  }, [customer, userPreferences, integratorMe, isIntegratorMeLoading]);

  if (user) {
    if (!customer?.keycloakId) {
      return null;
    }

    return (
      <FeatureFlagsProvider userContext={ldUserContext}>
        <PrivateRoutes />
      </FeatureFlagsProvider>
    );
  } else {
    return <PublicRoutes />;
  }
};
